import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowRight,
  Briefcase,
  Check,
  Eye,
  EyeOff,
  Lock,
  Phone,
  User,
  UserPlus,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks';
import { cn } from '@/utils/cn';
import { DEPARTMENTS } from '@/utils/constants';
import { type RegisterFormData, registerSchema } from '@/utils/validators';

export function Register() {
  const { register: registerUser, isLoading, error, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const password = watch('password', '');

  const passwordChecks = [
    { label: '8+ chars', valid: password.length >= 8 },
    { label: 'Uppercase', valid: /[A-Z]/.test(password) },
    { label: 'Lowercase', valid: /[a-z]/.test(password) },
    { label: 'Number', valid: /\d/.test(password) },
    { label: 'Symbol', valid: /[!@#$%^&*.]/.test(password) },
  ];

  const onSubmit = async (data: RegisterFormData) => {
    clearError();
    await registerUser(data);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white leading-tight">
          Join the <span className="text-gradient-brand">Fleet</span>
        </h2>
        <p className="text-sm font-black text-slate-400 dark:text-white/40 uppercase tracking-widest">
          Create your professional profile
        </p>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-4 rounded-xl bg-rose-50 dark:bg-rose-500/10 border-2 border-rose-100 dark:border-rose-500/20 flex items-start gap-3 backdrop-blur-xl"
        >
          <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0" />
          <p className="text-xs text-rose-700 dark:text-rose-300 font-black uppercase tracking-wide">
            {error}
          </p>
        </motion.div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label
              htmlFor="firstName"
              className="text-[10px] font-black text-neolab-green/60 dark:text-neolab-green uppercase tracking-[0.4em] ml-4"
            >
              First Name
            </label>
            <input
              id="firstName"
              {...register('firstName')}
              placeholder="John"
              className={cn(
                'input-field',
                errors.firstName && 'border-rose-500 focus:ring-rose-500/10',
              )}
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="lastName"
              className="text-[10px] font-black text-neolab-green/60 dark:text-neolab-green uppercase tracking-[0.4em] ml-4"
            >
              Last Name
            </label>
            <input
              id="lastName"
              {...register('lastName')}
              placeholder="Doe"
              className={cn(
                'input-field',
                errors.lastName && 'border-rose-500 focus:ring-rose-500/10',
              )}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="username"
            className="text-[10px] font-black text-neolab-green/60 dark:text-neolab-green uppercase tracking-[0.4em] ml-4"
          >
            Username
          </label>
          <div className="relative group">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-neolab-green transition-colors" />
            <input
              id="username"
              {...register('username')}
              placeholder="jdoe_neo"
              className={cn(
                'input-field pl-12',
                errors.username && 'border-rose-500 focus:ring-rose-500/10',
              )}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="phoneNumber"
            className="text-[10px] font-black text-neolab-green/60 dark:text-neolab-green uppercase tracking-[0.4em] ml-4"
          >
            Global Phone Access
          </label>
          <div className="relative group">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-neolab-green transition-colors" />
            <input
              id="phoneNumber"
              {...register('phoneNumber')}
              placeholder="+234..."
              className={cn(
                'input-field pl-12',
                errors.phoneNumber && 'border-rose-500 focus:ring-rose-500/10',
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label
              htmlFor="department"
              className="text-[10px] font-black text-neolab-green/60 dark:text-neolab-green uppercase tracking-[0.4em] ml-4"
            >
              Department
            </label>
            <select
              id="department"
              {...register('department')}
              className={cn(
                'input-field appearance-none px-4',
                errors.department && 'border-rose-500 focus:ring-rose-500/10',
              )}
            >
              <option value="">Select...</option>
              {DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label
              htmlFor="jobTitle"
              className="text-[10px] font-black text-neolab-green/60 dark:text-neolab-green uppercase tracking-[0.4em] ml-4"
            >
              Job Title
            </label>
            <div className="relative group">
              <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-neolab-green transition-colors" />
              <input
                id="jobTitle"
                {...register('jobTitle')}
                placeholder="Technician"
                className={cn(
                  'input-field pl-12',
                  errors.jobTitle && 'border-rose-500 focus:ring-rose-500/10',
                )}
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="password"
            className="text-[10px] font-black text-neolab-green/60 dark:text-neolab-green uppercase tracking-[0.4em] ml-4"
          >
            Password
          </label>
          <div className="relative group">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-neolab-green transition-colors" />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              {...register('password')}
              placeholder="••••••••"
              className={cn(
                'input-field pl-12 pr-12',
                errors.password && 'border-rose-500 focus:ring-rose-500/10',
              )}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-300 hover:text-neolab-green hover:bg-neolab-green/5 transition-all"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="flex flex-wrap gap-1.5 mt-2 ml-4">
            {passwordChecks.map((check, i) => (
              <div
                key={i}
                className={cn(
                  'flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all',
                  check.valid
                    ? 'bg-neolab-green text-white shadow-lg shadow-green-500/20'
                    : 'bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-white/20',
                )}
              >
                {check.valid ? <Check className="w-2 h-2" /> : <X className="w-2 h-2" />}
                {check.label}
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-4 flex items-center justify-center gap-3 group mt-4"
        >
          {isLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span className="text-sm">Provisioning Account...</span>
            </>
          ) : (
            <>
              <UserPlus className="w-5 h-5 transition-transform group-hover:scale-110" />
              <span className="text-sm">Register</span>
              <ArrowRight className="w-4 h-4 opacity-40 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>
      </form>

      <div className="pt-6 text-center">
        <p className="text-slate-400 dark:text-white/30 font-black text-[10px] uppercase tracking-widest">
          Already verified?{' '}
          <Link
            to="/login"
            className="text-neolab-green hover:dark:text-white hover:text-neolab-forest ml-2 font-black transition-all underline underline-offset-4 decoration-2"
          >
            Sign in here
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Register;
